import serverCall from "../../serverCall";

const getAllUser = (payload) => {
  try {
    const response = serverCall.post(`/admin/getall-user`, payload);
    return response;
  } catch (error) {
    throw error;
  }
};

const getUserDetails = (userId) => {
  try {
    const response = serverCall.get(`/admin/user/${userId}`);
    return response;
  } catch (error) {
    throw error;
  }
};

const createChat = (body, options) => {
  try {
    const response = serverCall.post(`/ai-conversation/create`, body, {
      signal: options?.signal,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

const createChatStream = (bodyOrFormData, onEvent, options) => {
  return new Promise(async (resolve, reject) => {
    try {
      const token = localStorage.getItem("linko_web");
      const baseURL = process.env.REACT_APP_BASE_URL?.replace(/\/$/, "") || "";

      let formData;
      if (bodyOrFormData instanceof FormData) {
        formData = bodyOrFormData;
      } else {
        formData = new FormData();
        if (bodyOrFormData?.inputText !== undefined && bodyOrFormData?.inputText !== null) {
          formData.append("inputText", bodyOrFormData.inputText);
        }
        if (bodyOrFormData?.suggestion) {
          formData.append("suggestion", bodyOrFormData.suggestion);
        }
        if (bodyOrFormData?.file) {
          formData.append("file", bodyOrFormData.file);
        }
      }

      const response = await fetch(`${baseURL}/ai-conversation/create`, {
        method: "POST",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: formData,
        signal: options?.signal,
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        return reject(new Error(errData?.message || `Request failed: ${response.status}`));
      }

      const contentType = response.headers.get("content-type") || "";

      if (contentType.includes("application/json")) {
        const data = await response.json();
        if (data?.status === false || data?.error || (data?.success === false && data?.message)) {
          return reject(new Error(data?.message || data?.error || "AI conversation failed"));
        }
        const d = data?.data || data;
        const text = typeof d === "string" ? d : (d?.translation || d?.text || d?.answer || "");
        const personalIdentifiersProtected = d?.personalIdentifiersProtected ?? (typeof data?.personalIdentifiersProtected === "number" ? data.personalIdentifiersProtected : null);
        if (onEvent) onEvent({ type: "done", translation: text, accumulated: text, personalIdentifiersProtected, raw: data });
        return resolve({ translation: text, personalIdentifiersProtected, raw: data });
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let currentEvent = "";
      const paragraphsMap = {};
      let accumulatedChunks = "";
      let result = { translation: "", personalIdentifiersProtected: null, raw: null };

      const getAccumulatedText = () => {
        const keys = Object.keys(paragraphsMap).map(Number).sort((a, b) => a - b);
        if (keys.length > 0) {
          return keys.map((k) => paragraphsMap[k]).join("\n\n");
        }
        return accumulatedChunks;
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split(/\r?\n/);
        buffer = lines.pop();

        for (const rawLine of lines) {
          const line = rawLine.trim();
          if (!line) {
            currentEvent = "";
            continue;
          }

          if (line.startsWith("event:")) {
            currentEvent = line.replace(/^event:\s*/, "").trim();
            continue;
          }

          if (line.startsWith("data:")) {
            const dataStr = line.replace(/^data:\s*/, "").trim();
            if (!dataStr) continue;
            if (dataStr === "[DONE]") {
              const finalTranslation = getAccumulatedText();
              result.translation = finalTranslation;
              if (onEvent) onEvent({ type: "done", translation: finalTranslation, accumulated: finalTranslation, personalIdentifiersProtected: result.personalIdentifiersProtected });
              continue;
            }

            try {
              const parsed = JSON.parse(dataStr);
              if (currentEvent === "error" || parsed?.error || (parsed?.status === false && parsed?.message)) {
                return reject(new Error(parsed?.message || parsed?.error || "AI conversation failed"));
              }
              if (parsed?.personalIdentifiersProtected !== undefined && parsed?.personalIdentifiersProtected !== null) {
                result.personalIdentifiersProtected = parsed.personalIdentifiersProtected;
              }
              if (parsed?.paragraphs && Array.isArray(parsed.paragraphs)) {
                parsed.paragraphs.forEach((p) => {
                  if (p?.index !== undefined && p?.translation !== undefined) {
                    paragraphsMap[p.index] = p.translation;
                  }
                });
              }

              if (currentEvent === "start") {
                if (onEvent) {
                  onEvent({
                    type: "start",
                    stage: parsed?.stage || "preparing",
                    source: parsed?.source || "text",
                    personalIdentifiersProtected: result.personalIdentifiersProtected,
                    raw: parsed,
                  });
                }
              } else if (currentEvent === "pii-complete") {
                if (onEvent) {
                  onEvent({
                    type: "pii-complete",
                    source: parsed?.source,
                    personalIdentifiersProtected: result.personalIdentifiersProtected,
                    raw: parsed,
                  });
                }
              } else if (currentEvent === "paragraph" || (parsed?.translation !== undefined && parsed?.index !== undefined)) {
                const pIdx = parsed.index ?? (Object.keys(paragraphsMap).length + 1);
                const pText = parsed.translation !== undefined ? parsed.translation : (parsed.text || "");
                paragraphsMap[pIdx] = pText;
                result.translation = getAccumulatedText();
                if (onEvent) {
                  onEvent({
                    type: "paragraph",
                    index: pIdx,
                    translation: pText,
                    accumulated: result.translation,
                    personalIdentifiersProtected: result.personalIdentifiersProtected,
                    raw: parsed,
                  });
                }
              } else if (currentEvent === "chunk" || parsed?.text !== undefined) {
                const chunkText = parsed.text !== undefined ? parsed.text : (parsed.translation || "");
                accumulatedChunks += chunkText;
                result.translation = getAccumulatedText();
                if (onEvent) {
                  onEvent({
                    type: "chunk",
                    text: chunkText,
                    accumulated: result.translation,
                    personalIdentifiersProtected: result.personalIdentifiersProtected,
                    raw: parsed,
                  });
                }
              } else if (currentEvent === "done" || parsed?.done || (parsed?.id && parsed?.total !== undefined)) {
                const finalTranslation = parsed?.translation || parsed?.text || parsed?.answer || getAccumulatedText();
                result.translation = finalTranslation;
                result.raw = parsed;
                if (onEvent) {
                  onEvent({
                    type: "done",
                    id: parsed?.id,
                    total: parsed?.total,
                    translation: finalTranslation,
                    accumulated: finalTranslation,
                    personalIdentifiersProtected: result.personalIdentifiersProtected,
                    raw: parsed,
                  });
                }
              } else {
                const textVal = parsed?.translation ?? parsed?.text ?? parsed?.answer ?? (typeof parsed?.data === "string" ? parsed.data : null);
                if (textVal !== null && textVal !== undefined) {
                  if (parsed?.index !== undefined) {
                    paragraphsMap[parsed.index] = textVal;
                  } else {
                    accumulatedChunks += textVal;
                  }
                  result.translation = getAccumulatedText();
                  if (onEvent) {
                    onEvent({
                      type: "chunk",
                      text: textVal,
                      accumulated: result.translation,
                      personalIdentifiersProtected: result.personalIdentifiersProtected,
                      raw: parsed,
                    });
                  }
                }
              }
            } catch (e) {
              accumulatedChunks += dataStr;
              result.translation = getAccumulatedText();
              if (onEvent) {
                onEvent({ type: "chunk", text: dataStr, accumulated: result.translation, personalIdentifiersProtected: result.personalIdentifiersProtected });
              }
            }
          }
        }
      }

      if (!result.translation) {
        result.translation = getAccumulatedText();
      }

      resolve(result);
    } catch (err) {
      reject(err);
    }
  });
};

const updateUser = (userId, body) => {
  try {
    const response = serverCall.put(`/admin/user/update/${userId}`, body);
    return response;
  } catch (error) {
    throw error;
  }
};

const deleteUser = (userId) => {
  try {
    const response = serverCall.delete(`/admin/user/delete/${userId}`);
    return response;
  } catch (error) {
    throw error;
  }
};

const aiChat = (body, options) => {
  try {
    const response = serverCall.post(`/ai-conversation/chat`, body, {
      signal: options?.signal,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

const aiChatAudioPreset = (body) => {
  try {
    const response = serverCall.post(`/ai-conversation/chat-audio-preset`, body);
    return response;
  } catch (error) {
    throw error;
  }
};

const aiChatTranslationPreset = (body) => {
  try {
    const response = serverCall.post(`/ai-conversation/chat-translation-preset`, body);
    return response;
  } catch (error) {
    throw error;
  }
};

const clearSession = () => {
  try {
    const response = serverCall.get(`/ai-conversation/inActiveSession`);
    return response;
  } catch (error) {
    throw error;
  }
};

const deleteAiConversations = () => {
  try {
    const response = serverCall.delete(`/users/deleteAidata/aiconversations`);
    return response;
  } catch (error) {
    throw error;
  }
};

/* const updateAdminProfile = (body, userId) => {
  try {
    const response = serverCall.patch(`/admin/update-profile`, body);
    return response;
  } catch (error) {
    throw error;
  }
}; */

/* const adminFileUpload = (body) => {
  try {
    const response = serverCall.post(`/company/update-company`, body);
    return response;
  } catch (error) {
    throw error;
  }
}; */

/* const getCompany = () => {
  try {
    const response = serverCall.get(`/company/get-companies`);
    return response;
  } catch (error) {
    throw error;
  }
}; */

const ChatService = {
  getAllUser,
  getUserDetails,
  createChat,
  createChatStream,
  aiChat,
  aiChatAudioPreset,
  aiChatTranslationPreset,
  updateUser,
  deleteUser,
  clearSession,
  deleteAiConversations,
};

export default ChatService;
